import React, { useState } from 'react';
import { Cube, Plus, PencilSimple, Trash, CheckCircle, CircleDashed, ShieldCheck, Clock, WarningCircle, XCircle, Sparkle, ArrowRight } from '@phosphor-icons/react';
import { ActionMenu } from '../ui/ActionMenu';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { cn, getSession, getUnconfirmedSuccesses } from '../../lib/utils';
import type { SavedProperty } from '../../lib/utils';
interface MyPropertiesListProps {
  properties: SavedProperty[];
  onToggleActive: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onAddManual: () => void;
  onGoToMarketplace: () => void;
}
export function MyPropertiesList({
  properties,
  onToggleActive,
  onEdit,
  onDelete,
  onAddManual,
  onGoToMarketplace
}: MyPropertiesListProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const propertyToDelete = confirmDeleteId ? properties.find(p => p.id === confirmDeleteId) : null;
  const session = getSession();
  const unconfirmedSuccesses = getUnconfirmedSuccesses(session?.email);
  // Empty state
  if (properties.length === 0) {
    return <div className="rounded-2xl bg-dark-2 border border-dark-3 p-6 sm:p-8 text-center" data-id="element-467">
        <Cube size={48} weight="regular" className="text-light-4 mx-auto mb-4" data-id="element-468" />
        <h3 className="text-base font-semibold text-light-1 mb-2" data-id="element-469">
          Proprietățile mele
        </h3>
        <p className="text-sm text-light-3 leading-relaxed max-w-md mx-auto mb-5" data-id="element-470">
          Salvează detaliile proprietăților pe care le închiriezi ca să trimiți
          oferte mai rapid. Pentru a adăuga prima proprietate, mergi în
          Marketplace și trimite o ofertă unui chiriaș.
        </p>
        <button type="button" onClick={onGoToMarketplace} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-471">
          Mergi în Marketplace
        </button>
      </div>;
  }
  // Populated list
  return <>
      <div className="rounded-2xl bg-dark-2 border border-dark-3 px-6 py-7" data-id="element-472">
        <div className="flex items-center justify-between mb-4" data-id="element-473">
          <h3 className="text-base font-semibold text-light-1" data-id="element-474">
            Proprietățile mele
          </h3>
          <span className="text-xs text-light-3" data-id="element-475">
            {properties.length}{' '}
            {properties.length === 1 ? 'proprietate' : 'proprietăți'}
          </span>
        </div>

        <ul className="space-y-3" data-id="element-476">
          {properties.map(p => {
          const facilitiesCount = p.facilities.location.length + p.facilities.essential.length + p.facilities.preferred.length;
          const metaParts = [p.priceLabel, p.locationLabel, `${facilitiesCount} ${facilitiesCount === 1 ? 'facilitate salvată' : 'facilități salvate'}`].filter(Boolean);
          const vStatus = p.verificationStatus?.status || 'unverified';
          const isUnconfirmed = unconfirmedSuccesses.some(u => u.property?.id === p.id);
          return <li key={p.id} className={cn('rounded-xl border p-3 sm:p-4 flex items-start gap-3 transition-colors', isUnconfirmed ? 'bg-lime-5/5 border-lime-4' : 'bg-dark-3 border-dark-4')} data-id="element-477">
                {/* Thumbnail */}
                <div className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-dark-4 flex items-center justify-center" data-id="element-478">
                  {p.thumbnail ? <img src={p.thumbnail} alt={p.name} className="w-full h-full object-cover" data-id="element-479" /> : <Cube size={28} weight="regular" className="text-light-4" data-id="element-480" />}
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0" data-id="element-481">
                  <div className="flex items-start justify-between gap-2 mb-1" data-id="element-482">
                    <div className="flex flex-wrap items-center gap-2 min-w-0" data-id="element-483">
                      <h4 className="text-sm font-semibold text-light-1 truncate" data-id="element-484">
                        {p.name}
                      </h4>
                      {isUnconfirmed && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-lime-5 text-lime-1 border border-lime-4" data-id="element-485">
                          <Sparkle size={12} weight="fill" data-id="element-486" /> Oferta acceptată —
                          confirmă <ArrowRight size={10} weight="bold" data-id="element-487" />
                        </span>}
                      {!isUnconfirmed && vStatus === 'verified' && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-lime-5/10 text-lime-1 border border-lime-5/20" data-id="element-488">
                          <ShieldCheck size={12} weight="fill" data-id="element-489" /> Verificată
                        </span>}
                      {!isUnconfirmed && vStatus === 'pending' && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20" data-id="element-490">
                          <Clock size={12} weight="fill" data-id="element-491" /> În verificare
                        </span>}
                      {!isUnconfirmed && vStatus === 'rejected' && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20" data-id="element-492">
                          <WarningCircle size={12} weight="fill" data-id="element-493" /> Respinsă
                        </span>}
                      {!isUnconfirmed && vStatus === 'expired' && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20" data-id="element-494">
                          <XCircle size={12} weight="fill" data-id="element-495" /> Expirată
                        </span>}
                    </div>
                    {/* Status pill — clickable to toggle */}
                    <button type="button" onClick={() => onToggleActive(p.id)} title={p.isActive ? 'Apasă pentru a marca inactivă' : 'Apasă pentru a marca activă'} className={cn('shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors cursor-pointer', p.isActive ? 'bg-lime-5 text-lime-1 border border-lime-4 hover:bg-lime-4' : 'bg-dark-4 text-light-3 border border-dark-4 hover:text-light-1')} data-id="element-496">
                      {p.isActive ? <CheckCircle size={10} weight="fill" data-id="element-497" /> : <CircleDashed size={10} weight="regular" data-id="element-498" />}
                      {p.isActive ? 'Activă' : 'Inactivă'}
                    </button>
                  </div>
                  <p className="text-xs text-light-3 leading-relaxed" data-id="element-499">
                    {metaParts.join(' · ')}
                  </p>
                </div>

                {/* Kebab */}
                <div className="shrink-0 -mt-1 -mr-1" data-id="element-500">
                  <ActionMenu triggerAriaLabel="Acțiuni proprietate" items={[{
                label: 'Editează detalii',
                icon: <PencilSimple size={18} weight="regular" data-id="element-502" />,
                onClick: () => onEdit(p.id)
              }, {
                label: p.isActive ? 'Marchează inactivă' : 'Marchează activă',
                icon: p.isActive ? <CircleDashed size={18} weight="regular" data-id="element-503" /> : <CheckCircle size={18} weight="regular" data-id="element-504" />,
                onClick: () => onToggleActive(p.id)
              }, {
                label: 'Șterge',
                icon: <Trash size={18} weight="regular" data-id="element-505" />,
                onClick: () => setConfirmDeleteId(p.id),
                destructive: true
              }]} data-id="element-501" />
                </div>
              </li>;
        })}
        </ul>

        <div className="pt-5 mt-2 border-t border-dark-3" data-id="element-506">
          <button type="button" onClick={onAddManual} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-507">
            <Plus size={14} weight="bold" data-id="element-508" />
            Adaugă manual
          </button>
        </div>
      </div>

      <ConfirmDialog open={!!confirmDeleteId} title="Ștergi această proprietate?" description={propertyToDelete ? `"${propertyToDelete.name}" va fi eliminată din lista ta. Nu poți reveni asupra acestei acțiuni.` : undefined} confirmLabel="Șterge" cancelLabel="Anulează" variant="danger" dashboardOffset onConfirm={() => {
      if (confirmDeleteId) onDelete(confirmDeleteId);
      setConfirmDeleteId(null);
    }} onCancel={() => setConfirmDeleteId(null)} data-id="element-509" />
    </>;
}